import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, track } from "@/lib/api";
import { toast } from "sonner";

const eur = v => v == null ? "—" : `€${Math.round(v).toLocaleString("it-IT")}`;
const pctv = v => v == null ? "—" : `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`;

const GROUPS = [
  { id: "ricavi", label: "Ricavi", fields: [
    { key: "price_change_pct", label: "Variazione prezzi (es. 0.03 = +3%)", type: "number" },
    { key: "volume_factor", label: "Fattore volumi (es. 0.99 = -1%)", type: "number" },
    { key: "volume_unchanged", label: "Volumi invariati (scelta esplicita)", type: "checkbox" }]},
  { id: "cogs", label: "Costo del venduto (COGS)", fields: [
    { key: "cogs_improvement_pp", label: "Miglioramento COGS in punti % (es. 0.015 = -1,5pp)", type: "number" },
    { key: "purchase_cost_change_pct", label: "Variazione costo acquisti (es. -0.05)", type: "number" }]},
  { id: "personale", label: "Personale", fields: [
    { key: "personnel_delta", label: "Variazione costo personale annua € (es. 20000 o -15000)", type: "number" },
    { key: "headcount_delta", label: "Variazione dipendenti (es. 1 o -1)", type: "number" },
    { key: "employer_cost_annual", label: "Costo aziendale annuo per dipendente €", type: "number" }]},
  { id: "opex", label: "Altri costi / Marketing", fields: [
    { key: "opex_delta_eur", label: "Variazione altri costi € (es. -10000)", type: "number" },
    { key: "marketing_spend_delta", label: "Incremento spesa marketing €", type: "number" },
    { key: "marketing_incremental_revenue", label: "Ricavi incrementali attesi da marketing €", type: "number" },
    { key: "marketing_contribution_margin", label: "Contribution margin ricavi incrementali (0-1)", type: "number" }]},
  { id: "circolante", label: "Capitale circolante (solo cassa)", fields: [
    { key: "target_dso", label: "DSO obiettivo (giorni)", type: "number" },
    { key: "target_inventory", label: "Magazzino obiettivo €", type: "number" }]},
  { id: "finanza", label: "Finanza / Investimenti", fields: [
    { key: "new_debt", label: "Nuovo debito €", type: "number" },
    { key: "debt_repayment", label: "Rimborso debito €", type: "number" },
    { key: "debt_rate", label: "Tasso interesse (es. 0.05)", type: "number" },
    { key: "capex", label: "Investimento (CAPEX) €", type: "number" },
    { key: "capex_useful_life_years", label: "Vita utile per ammortamento (anni)", type: "number" }]},
];

export default function CFOSimulator() {
  const [baseline, setBaseline] = useState(null);
  const [locked, setLocked] = useState(null);
  const [drivers, setDrivers] = useState({});
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState({});
  const [label, setLabel] = useState("Base");
  const [saved, setSaved] = useState([]);
  const [solverTarget, setSolverTarget] = useState("");
  const [solver, setSolver] = useState(null);
  const [solverLocked, setSolverLocked] = useState(false);

  useEffect(() => {
    api.get("/scenarios/v2/baseline")
      .then(r => setBaseline(r.data))
      .catch(e => { if (String(formatApiError(e)).includes("upgrade_required")) setLocked("grow"); else setLocked("error"); });
    api.get("/scenarios/v2").then(r => setSaved(r.data?.scenarios || [])).catch(() => {});
  }, []);

  const setD = (k, v) => setDrivers(prev => ({ ...prev, [k]: v }));
  const calculate = async () => {
    try {
      const clean = Object.fromEntries(Object.entries(drivers).filter(([, v]) => v !== undefined && v !== ""));
      const r = await api.post("/scenarios/v2/calculate", { drivers: clean });
      setResult(r.data.result);
      if (r.data.result.status === "OK") track("scenario_run", { scenario_type: "CFO_V2" });
      else toast.error(r.data.result.reason || `Stato: ${r.data.result.status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async () => {
    try {
      const clean = Object.fromEntries(Object.entries(drivers).filter(([, v]) => v !== undefined && v !== ""));
      await api.post("/scenarios/v2/save", { label, drivers: clean });
      toast.success(`Scenario "${label}" salvato`);
      api.get("/scenarios/v2").then(r => setSaved(r.data?.scenarios || [])).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const solve = async () => {
    try {
      const r = await api.post("/scenarios/v2/reverse-solve", { target: { monthly_net_income_delta: Number(solverTarget) * 1 || undefined, annual_ebitda_delta: Number(solverTarget) * 12 || undefined } });
      setSolver(r.data.solver);
    } catch (e) {
      if (String(formatApiError(e)).includes("upgrade_required")) setSolverLocked(true);
      else toast.error(formatApiError(e));
    }
  };

  if (locked) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cfo-locked">
      <h1 className="text-3xl font-extrabold">CFO Simulator</h1>
      <p className="text-[#524F5E] mt-3">Il simulatore economico-finanziario multi-driver è disponibile dal piano <b>GROW</b> (Reverse Solver dal piano PRO).</p>
      <Link to="/pricing" className="ic-btn-primary inline-block mt-6">Vedi i piani</Link>
    </div>
  );
  if (!baseline) return <div className="max-w-6xl mx-auto px-4 py-20 text-center">Caricamento baseline…</div>;
  if (baseline.detail) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><p className="text-[#524F5E]">{baseline.detail}</p></div>;

  const baseRows = ["revenue", "cogs", "gross_profit", "gross_margin", "personnel", "other_opex", "ebitda",
    "ebitda_margin", "da", "ebit", "financial_charges", "taxes", "net_income", "net_margin",
    "trade_receivables", "inventory", "financial_debt", "cash"];
  const LABELS = { revenue: "Fatturato", cogs: "COGS", gross_profit: "Margine lordo", gross_margin: "Margine lordo %",
    personnel: "Personale", other_opex: "Altri costi operativi", ebitda: "EBITDA", ebitda_margin: "Margine EBITDA %",
    da: "Ammortamenti", ebit: "EBIT", financial_charges: "Oneri finanziari", taxes: "Imposte",
    net_income: "Utile netto", net_margin: "Margine netto %", trade_receivables: "Crediti", inventory: "Magazzino",
    financial_debt: "Debiti finanziari", cash: "Liquidità" };
  const isPct = k => k.endsWith("_margin");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cfo-simulator">
      <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">CFO Simulator v2</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 mb-6">Simula sulle tue baseline reali</h1>

      {/* Baseline riconciliata */}
      <div className="ic-card p-6 mb-8" data-testid="cfo-baseline">
        <h2 className="font-bold mb-1">La tua baseline (periodo {baseline.financial_period_id})</h2>
        <p className="text-[10px] text-[#7A7788] mb-4">
          {baseline.reconciled
            ? `Riconciliata con il Financial Engine (tolleranza ${baseline.reconciliation_tolerance_pct}%). ${baseline.cogs_status ? `COGS: ${baseline.cogs_status === "SECTOR_PROXY" ? "stima di settore" : baseline.cogs_status}` : ""}`
            : `Baseline incompleta: mancano ${(baseline.missing_inputs || []).join(", ")} — completa i dati finanziari.`}
          {!baseline.bridge_complete && " · Ponte verso l'utile incompleto: lo scenario ricalcola EBITDA/EBIT, non l'utile netto."}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {baseRows.filter(k => baseline[k] != null).map(k => (
            <div key={k} className="bg-[#F4F4F7] rounded-xl p-3" data-testid={`cfo-base-${k}`}>
              <p className="text-[10px] uppercase tracking-widest text-[#7A7788]">{LABELS[k]}</p>
              <p className="font-extrabold">{isPct(k) ? pctv(baseline[k]) : eur(baseline[k])}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Leve (accordion) */}
      <h2 className="text-xl font-bold mb-3">Scegli le leve</h2>
      <div className="space-y-3 mb-6">
        {GROUPS.map(g => (
          <div key={g.id} className="ic-card overflow-hidden" data-testid={`lever-${g.id}`}>
            <button className="w-full text-left p-4 font-bold flex justify-between items-center"
              data-testid={`lever-toggle-${g.id}`}
              onClick={() => setOpen(prev => ({ ...prev, [g.id]: !prev[g.id] }))}>
              {g.label} <span className="text-[#7A7788]">{open[g.id] ? "−" : "+"}</span>
            </button>
            {open[g.id] && (
              <div className="px-4 pb-4 grid sm:grid-cols-2 gap-3">
                {g.fields.map(f => (
                  <label key={f.key} className="text-xs text-[#524F5E]">{f.label}
                    {f.type === "checkbox" ? (
                      <input type="checkbox" className="ml-2" data-testid={`drv-${f.key}`}
                        checked={!!drivers[f.key]} onChange={e => setD(f.key, e.target.checked || undefined)} />
                    ) : (
                      <input type="number" step="any" className="ic-input mt-1 w-full" data-testid={`drv-${f.key}`}
                        value={drivers[f.key] ?? ""} onChange={e => setD(f.key, e.target.value === "" ? undefined : Number(e.target.value))} />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <button className="ic-btn-primary mb-10" data-testid="cfo-calculate" onClick={calculate}>Calcola scenario</button>

      {/* Output Oggi / Scenario / Delta */}
      {result?.status === "OK" && (
        <div className="mb-10" data-testid="cfo-result">
          <h2 className="text-xl font-bold mb-3">Oggi vs Scenario</h2>
          <div className="ic-card overflow-x-auto mb-4">
            <table className="w-full text-xs sm:text-sm">
              <thead><tr className="text-left text-[#7A7788] text-xs uppercase tracking-widest">
                <th className="p-3">Linea</th><th className="p-3">Oggi</th><th className="p-3">Scenario</th><th className="p-3">Delta</th>
              </tr></thead>
              <tbody>
                {result.rows.map(r => (
                  <tr key={r.line} className={`border-t border-[#F4F4F7] ${r.line === "ebitda" || r.line === "net_income" ? "font-bold" : ""}`} data-testid={`cfo-row-${r.line}`}>
                    <td className="p-3">{r.label}</td>
                    <td className="p-3">{r.unit === "%" ? pctv(r.current) : eur(r.current)}</td>
                    <td className="p-3">{r.unit === "%" ? pctv(r.scenario) : eur(r.scenario)}</td>
                    <td className="p-3" style={{ color: r.delta > 0 ? "#10B981" : r.delta < 0 ? "#DC2626" : undefined }}>
                      {r.delta == null ? "—" : r.unit === "%" ? `${r.delta > 0 ? "+" : ""}${r.delta.toLocaleString("it-IT", { maximumFractionDigits: 2 })}pp` : `${r.delta > 0 ? "+" : ""}${eur(r.delta)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.net_income_status === "PARTIAL_BRIDGE" && (
            <p className="text-xs text-[#C2410C] bg-[#FFF0E8] border border-[#FF6A24]/40 rounded-lg p-3 mb-4">{result.net_income_note}</p>
          )}
          {Object.keys(result.cash_effects || {}).length > 0 && (
            <div className="ic-card p-5 mb-4 border-[#10B981]/30" data-testid="cfo-cash">
              <p className="text-xs uppercase tracking-widest text-[#7A7788] mb-2">Effetti cassa (separati dal conto economico — mai sommati)</p>
              {Object.values(result.cash_effects).map((c, i) => (
                <p key={i} className="text-sm font-bold text-[#10B981]">
                  {c.status === "OK" ? `${c.label}: ${eur(c.cash_release ?? c.cash_net ?? c.cash_out)}` : `${c.label || "Effetto cassa"}: dati insufficienti`}
                </p>
              ))}
            </div>
          )}
          <p className="text-[10px] text-[#7A7788] mb-4">{result.disclaimer}</p>
          <div className="flex flex-wrap items-center gap-3">
            <select className="ic-input" value={label} onChange={e => setLabel(e.target.value)} data-testid="cfo-label">
              <option>Conservativo</option><option>Base</option><option>Ambizioso</option>
            </select>
            <button className="ic-btn-primary" data-testid="cfo-save" onClick={save}>Salva scenario</button>
          </div>
        </div>
      )}

      {/* Scenari salvati + Reverse solver */}
      {saved.length > 0 && (
        <div className="mb-10" data-testid="cfo-saved">
          <h2 className="text-xl font-bold mb-3">Scenari salvati</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {saved.map(sv => (
              <div key={sv.scenario_v2_id} className="ic-card p-5">
                <p className="font-bold">{sv.label}</p>
                <p className="text-xs text-[#7A7788] mt-1">EBITDA scenario: <b>{eur(sv.result?.rows?.find(r => r.line === "ebitda")?.scenario)}</b></p>
                <p className="text-[10px] text-[#7A7788] mt-1">{String(sv.created_at).slice(0, 10)} · engine v{sv.scenario_engine_version}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ic-card p-6 border-[#5424D5]/30" data-testid="cfo-solver">
        <h2 className="text-xl font-bold">Come guadagno di più?</h2>
        <p className="text-sm text-[#524F5E] mt-1 mb-3">Reverse solver deterministico (piano PRO): inserisci il target mensile e ottieni le leve algebriche fattibili sulle tue baseline.</p>
        {solverLocked ? (
          <p className="text-sm text-[#7A7788]">Disponibile dal piano <b>PRO</b>. <Link to="/pricing" className="text-[#5424D5] font-semibold">Vedi i piani</Link></p>
        ) : (
          <div className="flex flex-wrap gap-3 items-center">
            <input type="number" className="ic-input w-48" placeholder="es. 1000" value={solverTarget}
              onChange={e => setSolverTarget(e.target.value)} data-testid="solver-target" />
            <span className="text-sm text-[#7A7788]">€ in più al mese</span>
            <button className="ic-btn-primary" data-testid="solver-run" onClick={solve}>Calcola leve</button>
          </div>
        )}
        {solver?.status === "OK" && (
          <div className="mt-4" data-testid="solver-result">
            <p className="text-sm font-semibold mb-2">{solver.copy}</p>
            {solver.note && <p className="text-xs text-[#C2410C] mb-2">{solver.note}</p>}
            {solver.solutions.filter(x => x.feasible).map((x, i) => (
              <div key={i} className="bg-[#F4F4F7] rounded-xl p-3 mb-2">
                <p className="text-sm font-bold">{x.label}: <span className="text-[#5424D5]">{x.change_required}</span></p>
                <p className="text-[10px] text-[#7A7788]">{x.detail}</p>
              </div>
            ))}
            <p className="text-[10px] text-[#7A7788] mt-2">{solver.disclaimer}</p>
          </div>
        )}
      </div>
    </div>
  );
}
